import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ChevronRight, Shield, Leaf } from 'lucide-react';
import { FAQAccordion } from '../../components/FAQAccordion';
import { CTAStrip } from '../../components/CTAStrip';
import { InlineCostCalculator } from '../../components/InlineCostCalculator';
const included = ['Full office strip-outs & furniture removal', 'IT equipment decommissioning', 'Confidential waste disposal', 'Out-of-hours & weekend service', 'Packing & labelling service', 'Waste transfer documentation', 'Recycling & responsible disposal', 'Single-item or full office moves'];
const idealFor = [{
  title: 'Office Relocations',
  desc: 'Moving to new premises with minimal downtime.'
}, {
  title: 'Office Refurbishments',
  desc: 'Clearing existing furniture before a refit.'
}, {
  title: 'Business Closures',
  desc: 'Complete strip-out and responsible disposal.'
}, {
  title: 'Downsizing',
  desc: 'Reducing office space and clearing surplus furniture.'
}];
const steps = [{
  num: '1',
  title: 'Site Survey',
  desc: 'We visit or assess via photos to understand the scope.'
}, {
  num: '2',
  title: 'Fixed Quote',
  desc: 'Transparent pricing with no hidden extras.'
}, {
  num: '3',
  title: 'Scheduled Clearance',
  desc: 'We work around your business hours, including weekends.'
}, {
  num: '4',
  title: 'Documentation',
  desc: 'Full waste transfer notes and recycling certificates provided.'
}];
const faqs = [{
  question: 'Can you work outside business hours?',
  answer: 'Yes. We regularly work evenings and weekends to minimise disruption to your business operations.'
}, {
  question: 'Do you handle IT equipment?',
  answer: 'We can remove IT equipment. For data destruction, we work with certified partners and can provide certificates of destruction.'
}, {
  question: 'How quickly can you clear an office?',
  answer: "Most offices can be cleared in 1–2 days. We'll give you a realistic timeline after assessing the job."
}, {
  question: 'Do you provide waste transfer notes?',
  answer: 'Yes. All commercial waste removals come with full duty of care documentation and waste transfer notes.'
}, {
  question: 'Can you handle multiple floors?',
  answer: 'Absolutely. We have the equipment and team to handle multi-floor clearances efficiently.'
}];
export function OfficeRemovalsPage() {
  return <div className="pt-16 lg:pt-20" data-id="element-963">
      <section className="relative h-72 md:h-96 overflow-hidden" data-id="element-964">
        <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&auto=format&fit=crop" alt="Office removals" className="w-full h-full object-cover" data-id="element-965" />
        <div className="absolute inset-0 hero-overlay" data-id="element-966" />
        <div className="absolute inset-0 flex items-center" data-id="element-967">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-968">
            <nav className="flex items-center gap-2 text-green-200 text-sm mb-4" data-id="element-969">
              <Link to="/" className="hover:text-white" data-id="element-970">
                Home
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-971" />
              <Link to="/services" className="hover:text-white" data-id="element-972">
                Services
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-973" />
              <span className="text-white" data-id="element-974">Office Removals</span>
            </nav>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-975">
              Office Removals & Clearances in London
            </h1>
            <p className="text-green-100 mt-3 text-lg" data-id="element-976">
              Minimal disruption. Maximum efficiency. From £200.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white" data-id="element-977">
        <div className="max-w-7xl mx-auto" data-id="element-978">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-979">
            <div data-id="element-980">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-981">
                What's Included
              </h2>
              <ul className="space-y-3" data-id="element-982">
                {included.map(item => <li key={item} className="flex items-center gap-3" data-id="element-983">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" style={{
                  color: '#1B8F4B'
                }} data-id="element-984" />
                    <span className="text-gray-700" data-id="element-985">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div data-id="element-986">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-987">
                Ideal For
              </h2>
              <div className="space-y-4" data-id="element-988">
                {idealFor.map(({
                title,
                desc
              }) => <div key={title} className="p-4 rounded-xl border border-gray-100 hover:border-[#1B8F4B] transition-colors" data-id="element-989">
                    <h3 className="font-bold text-gray-900 mb-1 text-sm" style={{
                  fontFamily: 'Montserrat, sans-serif'
                }} data-id="element-990">
                      {title}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-991">{desc}</p>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 texture-paper" data-id="element-992">
        <div className="max-w-7xl mx-auto" data-id="element-993">
          <h2 className="text-2xl font-bold text-gray-900 mb-10 text-center" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-994">
            Our Process
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-995">
            {steps.map(({
            num,
            title,
            desc
          }) => <div key={num} className="text-center" data-id="element-996">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg" style={{
              backgroundColor: '#1B8F4B'
            }} data-id="element-997">
                  {num}
                </div>
                <h3 className="font-bold text-gray-900 mb-2" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-998">
                  {title}
                </h3>
                <p className="text-sm text-gray-500" data-id="element-999">{desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white" data-id="element-1000">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1001">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-1002">
            Pricing
          </h2>
          <div className="rounded-2xl border-2 border-[#1B8F4B] p-8 mb-6" data-id="element-1003">
            <p className="text-4xl font-extrabold mb-2" style={{
            color: '#1B8F4B',
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-1004">
              From £200
            </p>
            <p className="text-gray-500 mb-6" data-id="element-1005">
              Guide price for a small office clearance. Final price confirmed
              after site survey.
            </p>
            <Link to="/contact" className="inline-block px-8 py-4 rounded-xl font-bold text-white transition-colors hover:opacity-90" style={{
            backgroundColor: '#1B8F4B',
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-1006">
              Request a Quote
            </Link>
          </div>
          <div className="flex justify-center gap-6" data-id="element-1007">
            <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-1008">
              <Shield className="w-4 h-4" style={{
              color: '#1B8F4B'
            }} data-id="element-1009" />{' '}
              Licensed & Fully Insured
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-1010">
              <Leaf className="w-4 h-4" style={{
              color: '#1B8F4B'
            }} data-id="element-1011" />{' '}
              Eco-Friendly Disposal
            </div>
          </div>
        </div>
      </section>

      <InlineCostCalculator defaultService="office-removals" serviceName="Office Removals" data-id="element-1012" />

      <section className="py-16 px-4 bg-white" data-id="element-1013">
        <div className="max-w-3xl mx-auto" data-id="element-1014">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-1015">
            Frequently Asked Questions
          </h2>
          <FAQAccordion items={faqs} data-id="element-1016" />
        </div>
      </section>

      <CTAStrip heading="Ready to Book Your Office Clearance?" subheading="Get a free, no-obligation quote. We reply within 30 minutes." data-id="element-1017" />
    </div>;
}